import { rawLocalStorage } from "@sfc/ui/local-storage";
import { createExternalStore } from "@client/utils/createExternalStore";
import { useCurrentUser } from "@client/hooks/use-current-user";

// Dev-only playback-source override (see ForceStreamingToggle): when on,
// useVideoSource presents this device's downloaded videos as absent so every
// player exercises the full remote/streaming pipeline, without deleting the
// downloads. Device-scoped and persisted in localStorage — like the download
// store it overrides, this is a property of the device, not of the signed-in
// (or impersonated) user. Mirrors muteStore/playbackRateStore mechanics.

const STORAGE_KEY = "video-force-streaming";

function readStoredForceStreaming(): boolean {
  try {
    return rawLocalStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // localStorage can throw (privacy mode / storage disabled); the override
    // is a dev tool, so default to normal behavior.
    return false;
  }
}

const store = createExternalStore<boolean>(readStoredForceStreaming());

// Raw stored value — for the toggle UI's checked state only. Playback
// consumers must use useForceStreamingActive below.
export function useForceStreaming(): boolean {
  return store.useValue();
}

// Whether the override should actually be APPLIED: the stored flag AND the
// real logged-in user (not the impersonated one) being an SFC admin — the
// same gate that shows the toggle. The flag is device-persisted, so without
// this an admin who left it on (or a de-adminned account) would strand a
// non-admin on the same device streaming-only — offline, their downloads
// would read "unavailable" — with no visible control to turn it off.
// useCurrentUser resolves from the offline identity cache on the iPad, so
// the gate holds offline too; while user data is still loading the override
// is inactive, which errs toward normal (local-preferring) playback.
export function useForceStreamingActive(): boolean {
  const forceStreaming = store.useValue();
  const { data: currentUser } = useCurrentUser();
  return forceStreaming && currentUser?.isSfcAdmin === true;
}

export function setForceStreaming(forceStreaming: boolean): void {
  store.set(forceStreaming);

  try {
    rawLocalStorage.setItem(STORAGE_KEY, String(forceStreaming));
  } catch {
    // Best-effort persistence; the in-memory store still applies app-wide.
  }
}
